/**
 * Downloads an ICS calendar file for the given event post
 * @param post - The post object containing event data
 */

import { Post } from '@frontend/data-access';

// Utility function to strip HTML tags and convert to plain text
export const stripHtmlTags = (html: string): string => {
  if (!html) return '';

  // Replace closing tags of block-level elements with newlines to preserve formatting
  const htmlWithLineBreaks = html
    .replace(/<\/(p|div|h[1-6]|li|blockquote|pre)>/gi, '\n')
    .replace(/<\/(ul|ol)>/gi, '\n\n')
    .replace(/<br\s*\/?>/gi, '\n');

  // Create a temporary div element to use browser's HTML parsing
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlWithLineBreaks;

  // Get the text content which strips all HTML tags
  const textContent = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up multiple consecutive newlines and trim
  return textContent.replace(/\n\s*\n\s*\n/g, '\n\n').trim();
};

export const calculateICS = ({
  post,
  recurrenceConfig,
}: {
  post: Post;
  recurrenceConfig?: string;
}) => {
  // Helper to format date as YYYYMMDDTHHmmssZ (UTC)
  function formatICSDate(dateString: string) {
    const date = new Date(dateString);
    return date
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}Z$/, 'Z');
  }

  const dtStart = formatICSDate(post.eventStartDate);
  const dtEnd = formatICSDate(post.eventEndDate);

  // Generate a UID for the event (required by ICS spec)
  const uid =
    (post.id || Math.random().toString(36).substring(2)) + '@fintechbridge.com';

  // Escape text for ICS
  function escapeICSText(text: string) {
    return (text || '')
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r\n|\r|\n/g, '\\n');
  }

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FintechBridge//Event//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `SUMMARY:${escapeICSText(post.title)}`,
    `DESCRIPTION:${escapeICSText(stripHtmlTags(post.body))}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    post.eventLocation ? `LOCATION:${escapeICSText(post.eventLocation)}` : '',
    recurrenceConfig ? `${recurrenceConfig}` : '',
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'TRIGGER:-PT15M',
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION: ${escapeICSText(post.title)} (1 hour before)`,
    'TRIGGER:-PT1H',
    'END:VALARM',
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION: ${escapeICSText(post.title)} (1 day before)`,
    'TRIGGER:-P1D',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n');

  return icsContent;
};

export const downloadICSFile = (post: Post) => {
  const icsContent = post?.recurrenceConfig
    ? post?.recurrenceConfig
    : calculateICS({
        post: post,
      });

  const blob = new Blob([icsContent], {
    type: 'text/calendar;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${post.title || 'event'}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
